/**
 * File Hashing Utility
 *
 * Computes SHA-256 hash of files for deduplication across devices.
 * Pattern based on existing implementation in use-background-removal.ts
 */

/**
 * Computes SHA-256 hash of a file for deduplication
 *
 * @param file - File or Blob to hash
 * @returns Hex string of SHA-256 hash
 *
 * @example
 * ```typescript
 * const file = new File(['content'], 'file.txt');
 * const hash = await computeFileHash(file);
 * console.log(hash); // "ed7002b439e9ac845f22357d822bac1444730fbdb6016d3ec9432297b9ec9f73"
 * ```
 */
export async function computeFileHash(file: File | Blob): Promise<string> {
  // Convert file to ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();

  // Compute SHA-256 hash
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);

  // Convert hash to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

/**
 * Generates a short hash for display purposes (first 8 characters)
 *
 * @param hash - Full SHA-256 hash
 * @returns First 8 characters of hash
 *
 * @example
 * ```typescript
 * const fullHash = "ed7002b439e9ac845f22357d822bac1444730fbdb6016d3ec9432297b9ec9f73";
 * const shortHash = getShortHash(fullHash);
 * console.log(shortHash); // "ed7002b4"
 * ```
 */
export function getShortHash(hash: string): string {
  return hash.substring(0, 8);
}

/**
 * Checks if two files have the same hash (are duplicates)
 *
 * @param file1 - First file
 * @param file2 - Second file
 * @returns True if files have same hash
 */
export async function areFilesDuplicate(file1: File | Blob, file2: File | Blob): Promise<boolean> {
  const hash1 = await computeFileHash(file1);
  const hash2 = await computeFileHash(file2);
  return hash1 === hash2;
}
